import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Article, Clock } from "@phosphor-icons/react";
import { api } from "../lib/api";
import Seo from "../components/Seo";

/**
 * Public blog index — primary SEO landing surface.
 * Lists all published posts with excerpt, tags, and read-time.
 */
export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/blog");
        setPosts(r.data?.posts || []);
      } catch (e) {
        setError("Failed to load posts. Please try again.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="min-h-screen bg-app text-primary-fg">
      <Seo
        title="Blog · Breakout Scanner — NSE Trading Education"
        description="Free guides on multi-year breakouts, cup & handle patterns, risk-reward ratio, position sizing, and how to scan NSE stocks like a pro."
        path="/blog"
      />
      <div className="max-w-3xl mx-auto px-4 py-10 sm:py-14">
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-muted-fg hover:text-primary-fg transition mb-6"
          data-testid="blog-back-home"
        >
          <ArrowLeft size={11} weight="bold" />
          Home
        </Link>

        <div className="flex items-center gap-2 mb-1">
          <Article size={20} weight="fill" className="text-pos" />
          <h1 className="text-3xl sm:text-4xl font-medium tracking-tight">Trading Library</h1>
        </div>
        <p className="text-sm text-secondary-fg leading-relaxed mb-10 max-w-xl">
          Deep guides on breakout patterns, position sizing, and how to use our
          scanner to find high-probability setups on NSE.
        </p>

        {loading && (
          <div className="text-xs font-mono uppercase tracking-widest text-muted-fg animate-pulse" data-testid="blog-loading">
            /// Loading posts…
          </div>
        )}
        {error && (
          <div className="border border-neg/50 bg-neg-soft/20 rounded-sm p-4 text-sm text-neg" data-testid="blog-error">
            {error}
          </div>
        )}

        {!loading && !error && posts.length === 0 && (
          <div className="border border-subtle rounded-sm p-6 text-center text-sm text-muted-fg" data-testid="blog-empty">
            No posts yet — check back soon.
          </div>
        )}

        <div className="space-y-6" data-testid="blog-list">
          {posts.map((p) => {
            // Rough read-time: ~200 wpm of markdown content (excerpt as proxy)
            const wc = (p.content_markdown?.split(/\s+/).length) || (p.excerpt?.split(/\s+/).length * 8) || 200;
            const minutes = Math.max(2, Math.round(wc / 200));
            return (
              <article
                key={p.slug}
                className="border-l-2 border-subtle hover:border-pos transition pl-5 py-1 group"
                data-testid={`blog-post-${p.slug}`}
              >
                <Link to={`/blog/${p.slug}`} className="block">
                  <div className="flex items-center gap-3 text-[10px] font-mono uppercase tracking-widest text-muted-fg mb-2">
                    <span className="flex items-center gap-1">
                      <Clock size={10} />
                      {minutes} min read
                    </span>
                    {p.tags?.slice(0, 2).map((t) => (
                      <span key={t} className="text-pos/70">#{t}</span>
                    ))}
                  </div>
                  <h2 className="text-xl sm:text-2xl font-medium leading-snug mb-2 group-hover:text-pos transition">
                    {p.title}
                  </h2>
                  <p className="text-sm text-secondary-fg leading-relaxed mb-3 line-clamp-3">
                    {p.excerpt}
                  </p>
                  <div className="inline-flex items-center gap-1 text-[11px] font-mono uppercase tracking-widest text-pos group-hover:gap-2 transition-all">
                    Read more
                    <ArrowRight size={11} weight="bold" />
                  </div>
                </Link>
              </article>
            );
          })}
        </div>

        <div className="mt-14 border-t border-subtle pt-6 text-[11px] text-muted-fg leading-relaxed">
          Want a topic covered? Submit a request via the{" "}
          <Link to="/feedback" className="text-pos hover:underline">Feedback page</Link>.
        </div>
      </div>
    </div>
  );
}
